import React from 'react';
import { Table, flexRender } from '@tanstack/react-table';
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { DataTableColumn, FilterRule } from '../../../types/grid';
import { ColumnHeaderMenu } from '../ColumnHeaderMenu';
import { HedgehogSpinner } from '../../brand/HedgehogSpinner';
import { Button } from '../../ui/Button';

interface DataTableGridRendererProps<T extends Record<string, any>> {
  table: Table<T>;
  columns: DataTableColumn<T>[];
  paginatedData: T[];
  isMobile: boolean;
  rowHeightClass: string;
  activeFilterRules: FilterRule[];
  openFilterColId: string | null;
  setOpenFilterColId: (id: string | null) => void;
  handleColumnFilterChange: (colKey: string, newRules: FilterRule[]) => void;
  handleColumnSort: (colId: string, direction: 'asc' | 'desc' | false) => void;
  onRowClick?: (row: T) => void;
  setActiveCell: (cell: { rowId: string; colId: string } | null) => void;
  setEditingCell: (cell: { rowId: string; colId: string } | null) => void;
  canEdit?: boolean;
  isLoading?: boolean;
  emptyState?: React.ReactNode;
  emptyTitle?: string;
  emptySubtitle?: string;
}

export function DataTableGridRenderer<T extends Record<string, any>>({
  table,
  columns,
  paginatedData,
  isMobile,
  rowHeightClass,
  activeFilterRules,
  openFilterColId,
  setOpenFilterColId,
  handleColumnFilterChange,
  handleColumnSort,
  onRowClick,
  setActiveCell,
  setEditingCell,
  canEdit = false,
  isLoading = false,
  emptyState,
  emptyTitle = 'No records found',
  emptySubtitle = 'Try adjusting your search or filters to find what you are looking for.',
}: DataTableGridRendererProps<T>) {
  const visibleLeafColumns = table.getVisibleLeafColumns();
  const columnLayoutMap: Record<string, { leftOffset?: number; rightOffset?: number; isLastBeforeRightPinned: boolean }> = {};

  let currentLeft = 0;
  for (let i = 0; i < visibleLeafColumns.length; i++) {
    const col = visibleLeafColumns[i];
    const meta = (col.columnDef.meta as any) || {};
    const isPinnedLeft = !isMobile && (meta.pinned === 'left' || col.id === 'select');
    if (isPinnedLeft) {
      columnLayoutMap[col.id] = { leftOffset: currentLeft, isLastBeforeRightPinned: false };
      currentLeft += col.getSize();
    }
  }

  let currentRight = 0;
  for (let i = visibleLeafColumns.length - 1; i >= 0; i--) {
    const col = visibleLeafColumns[i];
    const meta = (col.columnDef.meta as any) || {};
    const isPinnedRight = !isMobile && (meta.pinned === 'right' || col.id === 'actions');
    if (isPinnedRight) {
      columnLayoutMap[col.id] = {
        ...(columnLayoutMap[col.id] || { isLastBeforeRightPinned: false }),
        rightOffset: currentRight,
      };
      currentRight += col.getSize();
    }
  }

  for (let i = 0; i < visibleLeafColumns.length; i++) {
    const col = visibleLeafColumns[i];
    const meta = (col.columnDef.meta as any) || {};
    const isPinnedRight = !isMobile && (meta.pinned === 'right' || col.id === 'actions');
    let isLast = false;
    if (!isPinnedRight && i < visibleLeafColumns.length - 1) {
      const nextMeta = (visibleLeafColumns[i + 1].columnDef.meta as any) || {};
      const nextPinnedRight = !isMobile && (nextMeta.pinned === 'right' || visibleLeafColumns[i + 1].id === 'actions');
      isLast = nextPinnedRight;
    }
    columnLayoutMap[col.id] = {
      ...(columnLayoutMap[col.id] || {}),
      isLastBeforeRightPinned: isLast,
    };
  }

  return (
    <div
      className="flex-1 overflow-auto min-w-0 w-full relative z-0 bg-cf-surface overscroll-x-contain"
      style={{
        overscrollBehaviorX: 'contain',
        WebkitOverflowScrolling: 'touch',
      }}
      onWheel={(e) => {
        const target = e.currentTarget;
        if (!target) return;
        const canScrollHorizontally = target.scrollWidth > target.clientWidth;
        if (!canScrollHorizontally) return;

        // 1. Shift key + wheel (horizontal scroll on standard mice)
        if (e.shiftKey && e.deltaY !== 0) {
          target.scrollLeft += e.deltaY;
          return;
        }

        // 2. Vertical wheel over header or when at vertical boundaries
        if (Math.abs(e.deltaX) === 0 && e.deltaY !== 0) {
          const { scrollTop, scrollHeight, clientHeight } = target;
          const isAtVerticalBoundary =
            (e.deltaY > 0 && scrollTop + clientHeight >= scrollHeight - 1) ||
            (e.deltaY < 0 && scrollTop <= 1);
          const isHeader = (e.target as HTMLElement)?.closest('thead');
          if (isHeader || isAtVerticalBoundary) {
            target.scrollLeft += e.deltaY;
          }
        }
      }}
    >
      <table
        className="cf-table-grid bg-cf-surface text-xs table-fixed border-separate border-spacing-0 font-sans"
        style={{ width: `${table.getTotalSize()}px`, minWidth: '100%' }}
      >
        {/* Table Header */}
        <thead className="bg-[#f1f3f6] dark:bg-[#2e323c] bg-cf-tableHeader">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="bg-[#f1f3f6] dark:bg-[#2e323c] bg-cf-tableHeader">
              {headerGroup.headers.map((header) => {
                const colDef = header.column.columnDef;
                const meta = (colDef.meta as any) || {};
                const isPinnedLeft = !isMobile && (meta.pinned === 'left' || header.id === 'select');
                const isPinnedRight = !isMobile && (meta.pinned === 'right' || header.id === 'actions');
                const isSortable = header.column.getCanSort();
                const sortDirection = header.column.getIsSorted();

                const layout = columnLayoutMap[header.id] || { isLastBeforeRightPinned: false };
                const leftOffset = layout.leftOffset;
                const rightOffset = layout.rightOffset;
                const isLastBeforeRightPinned = layout.isLastBeforeRightPinned;

                const stickyHeaderClass = isPinnedLeft
                  ? 'sticky z-30 bg-[#f1f3f6] dark:bg-[#2e323c] bg-cf-tableHeader shadow-[2px_0_5px_rgba(0,0,0,0.1)]'
                  : isPinnedRight
                  ? 'sticky z-30 bg-[#f1f3f6] dark:bg-[#2e323c] bg-cf-tableHeader shadow-[-2px_0_5px_rgba(0,0,0,0.1)] text-right'
                  : 'sticky z-20 bg-[#f1f3f6] dark:bg-[#2e323c] bg-cf-tableHeader';

                const isSelectCol = header.id === 'select';

                return (
                  <th
                    key={header.id}
                    style={{
                      width: header.getSize(),
                      minWidth: colDef.minSize || header.getSize(),
                      maxWidth: colDef.maxSize || 320,
                      left: isPinnedLeft && leftOffset !== undefined ? `${leftOffset}px` : undefined,
                      right: isPinnedRight && rightOffset !== undefined ? `${rightOffset}px` : undefined,
                      top: 0,
                    }}
                    className={`group/header py-2 ${
                      isSelectCol
                        ? 'px-0 text-center'
                        : isLastBeforeRightPinned
                        ? 'pl-3 pr-5 text-left'
                        : isPinnedRight
                        ? 'px-3 text-left'
                        : 'px-3 text-left'
                    } text-xs font-semibold text-cf-textSecondary border-b border-cf-border dark:border-[#2e323c] select-none whitespace-nowrap overflow-hidden ${stickyHeaderClass}`}
                  >
                    {header.isPlaceholder ? null : isSelectCol ? (
                      <div className="flex items-center justify-center w-full h-full">
                        {typeof colDef.header === 'function'
                          ? colDef.header(header.getContext())
                          : colDef.header}
                      </div>
                    ) : (() => {
                      const originalCol = columns.find((c) => c.id === header.id);
                      const isFilterable =
                        !meta.isSystem &&
                        header.id !== 'select' &&
                        header.id !== 'actions' &&
                        originalCol?.filterable !== false;
                      const colDataType = meta.dataType || originalCol?.dataType || 'text';
                      const colOptions = originalCol?.options;
                      const colRules = activeFilterRules.filter((r) => r.columnKey === header.id);
                      const colDisplayName =
                        typeof colDef.header === 'string'
                          ? colDef.header
                          : header.id.replace(/^data_/, '');

                      return (
                        <div
                          className={`flex items-center ${
                            isPinnedRight ? 'justify-end' : 'justify-between'
                          } gap-1.5 w-full`}
                        >
                          <div
                            onClick={isSortable ? header.column.getToggleSortingHandler() : undefined}
                            className={`flex-1 min-w-0 flex items-center gap-1 ${
                              isSortable ? 'cursor-pointer hover:text-cf-textPrimary transition-colors' : ''
                            }`}
                            title={typeof colDef.header === 'string' ? colDef.header : undefined}
                          >
                            <span className="whitespace-nowrap overflow-visible font-sans">
                              {typeof colDef.header === 'function'
                                ? colDef.header(header.getContext())
                                : colDef.header || (header.id === 'actions' ? 'Actions' : '')}
                            </span>
                          </div>

                          {(isSortable || isFilterable) && (
                            <div
                              className="flex items-center gap-0.5 shrink-0"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {isSortable && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon-xs"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    header.column.toggleSorting();
                                  }}
                                  className={`p-1 cursor-pointer transition-all flex items-center justify-center ${
                                    sortDirection
                                      ? 'text-cf-orange bg-cf-orange/10 hover:bg-cf-orange/20'
                                      : 'text-cf-textMuted hover:text-cf-textPrimary hover:bg-cf-surfaceHover opacity-70 md:opacity-0 md:group-hover/header:opacity-70 md:hover:!opacity-100'
                                  }`}
                                  title={
                                    sortDirection === 'asc'
                                      ? `Sorted ascending. Click to sort descending.`
                                      : sortDirection === 'desc'
                                      ? `Sorted descending. Click to clear sorting.`
                                      : `Sort by ${colDisplayName}`
                                  }
                                >
                                  {sortDirection === 'asc' ? (
                                    <ArrowUp size={12} className="text-cf-orange" />
                                  ) : sortDirection === 'desc' ? (
                                    <ArrowDown size={12} className="text-cf-orange" />
                                  ) : (
                                    <ArrowUpDown size={11} />
                                  )}
                                </Button>
                              )}

                              {isFilterable && (
                                <ColumnHeaderMenu
                                  columnKey={header.id}
                                  columnName={colDisplayName}
                                  dataType={colDataType}
                                  options={colOptions}
                                  isSortable={isSortable}
                                  sortDirection={sortDirection}
                                  onSort={(dir) => handleColumnSort(header.id, dir)}
                                  isFilterable={isFilterable}
                                  rules={colRules}
                                  onRulesChange={(newRules) =>
                                    handleColumnFilterChange(header.id, newRules)
                                  }
                                  isOpen={openFilterColId === header.id}
                                  onToggleOpen={(open) =>
                                    setOpenFilterColId(open ? header.id : null)
                                  }
                                />
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>

        {/* Table Body */}
        <tbody>
          {isLoading && paginatedData.length === 0 ? (
            <tr>
              <td colSpan={visibleLeafColumns.length} className="h-64 text-center py-12">
                <div className="flex items-center justify-center h-full">
                  <HedgehogSpinner size="md" label="LOADING TABLE RECORDS..." />
                </div>
              </td>
            </tr>
          ) : paginatedData.length === 0 ? (
            <tr>
              <td colSpan={visibleLeafColumns.length} className="text-center py-12">
                {emptyState ? (
                  <div className="flex items-center justify-center p-6 w-full h-full min-h-[220px]">
                    {emptyState}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-48 text-center px-4">
                    <div className="text-sm font-semibold text-cf-textPrimary mb-1">{emptyTitle}</div>
                    <div className="text-xs text-cf-textSecondary max-w-sm">{emptySubtitle}</div>
                  </div>
                )}
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map((row) => {
              const isSelected = row.getIsSelected();
              const rowBgClass = isSelected
                ? 'bg-cf-surfaceActive hover:bg-cf-surfaceActive'
                : 'bg-cf-surface hover:bg-cf-surfaceHover';

              return (
                <tr
                  key={row.id}
                  onClick={() => onRowClick?.(row.original)}
                  className={`group transition-colors border-b border-cf-border ${rowHeightClass} ${rowBgClass} ${
                    onRowClick ? 'cursor-pointer' : ''
                  }`}
                >
                  {row.getVisibleCells().map((cell) => {
                    const colDef = cell.column.columnDef;
                    const meta = (colDef.meta as any) || {};
                    const isPinnedLeft = !isMobile && (meta.pinned === 'left' || cell.column.id === 'select');
                    const isPinnedRight = !isMobile && (meta.pinned === 'right' || cell.column.id === 'actions');
                    const shouldTruncate = meta.truncate !== false;

                    const layout = columnLayoutMap[cell.column.id] || { isLastBeforeRightPinned: false };
                    const leftOffset = layout.leftOffset;
                    const rightOffset = layout.rightOffset;
                    const isLastBeforeRightPinned = layout.isLastBeforeRightPinned;

                    // Enforce Invariant 16: group-hover:bg-cf-surfaceHover on every cell
                    const cellBgClass = isSelected
                      ? 'bg-cf-surfaceActive group-hover:bg-cf-surfaceActive'
                      : 'bg-cf-surface group-hover:bg-cf-surfaceHover';

                    const stickyCellClass = isPinnedLeft
                      ? `sticky z-10 ${cellBgClass} border-b border-cf-border shadow-[2px_0_5px_rgba(0,0,0,0.1)]`
                      : isPinnedRight
                      ? `sticky z-10 ${cellBgClass} border-b border-cf-border shadow-[-2px_0_5px_rgba(0,0,0,0.1)] text-right`
                      : `${cellBgClass} border-b border-cf-border/40`;

                    return (
                      <td
                        key={cell.id}
                        style={{
                          width: cell.column.getSize(),
                          minWidth: colDef.minSize || cell.column.getSize(),
                          maxWidth: colDef.maxSize || 320,
                          left: isPinnedLeft && leftOffset !== undefined ? `${leftOffset}px` : undefined,
                          right: isPinnedRight && rightOffset !== undefined ? `${rightOffset}px` : undefined,
                        }}
                        onClick={() => setActiveCell({ rowId: row.id, colId: cell.column.id })}
                        onDoubleClick={(e) => {
                          e.stopPropagation();
                          if (canEdit && !isPinnedLeft && !isPinnedRight && meta.editable !== false) {
                            setEditingCell({ rowId: row.id, colId: cell.column.id });
                          }
                        }}
                        className={`py-1 ${
                          cell.column.id === 'select'
                            ? 'px-0 text-center'
                            : isLastBeforeRightPinned
                            ? 'pl-2.5 pr-5'
                            : isPinnedRight
                            ? 'px-2.5'
                            : 'px-2.5'
                        } whitespace-nowrap transition-colors ${
                          shouldTruncate ? 'overflow-hidden text-ellipsis' : 'overflow-visible'
                        } ${stickyCellClass}`}
                      >
                        {flexRender(colDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
